import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export type ReturnLinkOutcome = 'VIEW' | 'SIGN_IN' | 'INVALID';

export interface ReturnLinkOpening {
  outcome: ReturnLinkOutcome;
  canClaim: boolean;
  renewed: boolean;
}

export type ReturnLinkClaimOutcome =
  | 'claimed'
  | 'merged'
  | 'already_claimed'
  | 'not_live'
  | 'staff'
  | 'not_signed_in'
  | 'failed';

/*
 * Written here rather than in a .graphql file: codegen reads the deployed dev
 * schema, which gains these two mutations only when this change is deployed.
 * The shapes below are the schema's (report-share.graphql, AUTH.9).
 *
 * The operation name OpenReturnLink is on graphql.module's skip list, so the
 * call is not queued behind session start-up: it needs no session.
 */
const OPEN_RETURN_LINK = gql`
  mutation OpenReturnLink($returnToken: ID!) {
    openReturnLink(returnToken: $returnToken) {
      outcome
      canClaim
      renewed
    }
  }
`;

const CLAIM_RETURN_LINK = gql`
  mutation ClaimReturnLink($returnToken: ID!) {
    claimReturnLink(returnToken: $returnToken) {
      outcome
    }
  }
`;

const INVALID: ReturnLinkOpening = {
  outcome: 'INVALID',
  canClaim: false,
  renewed: false
};

/**
 * AUTH.9. The two server calls behind old goals report links.
 *
 * Neither ever errors to its caller. Under `errorPolicy: 'all'` a refusal
 * resolves with `data: null` rather than throwing, and treating that as
 * anything but a failure is what turned an expired link into a silent bounce;
 * so a missing answer is INVALID here, and a failed claim is 'failed'.
 */
@Injectable({ providedIn: 'root' })
export class ReturnLinkDataService {
  public constructor(private readonly apollo: Apollo) {}

  public open(returnToken: string): Observable<ReturnLinkOpening> {
    return this.apollo
      .mutate<{ openReturnLink: ReturnLinkOpening }>({
        mutation: OPEN_RETURN_LINK,
        variables: { returnToken }
      })
      .pipe(
        map((result) => result?.data?.openReturnLink ?? INVALID),
        catchError(() => of(INVALID))
      );
  }

  public claim(returnToken: string): Observable<ReturnLinkClaimOutcome> {
    return this.apollo
      .mutate<{ claimReturnLink: { outcome: ReturnLinkClaimOutcome } }>({
        mutation: CLAIM_RETURN_LINK,
        variables: { returnToken }
      })
      .pipe(
        map((result) => result?.data?.claimReturnLink?.outcome ?? 'failed'),
        catchError(() => of('failed' as const))
      );
  }
}
